import { Button, Form, Modal, Select, Tooltip, Upload } from "antd";
import { message } from "utils/ciq-message";
import { ESubmittalStatus, UserParticipationType } from "constants/index";
import { TFetureDetailUpdateAction } from "entity-app/models";
import { useContext, useEffect, useMemo, useState } from "react";
import { ProjectContext } from "context/ProjectProvider";
import TextArea from "antd/lib/input/TextArea";
import {
  CheckCircleFilled,
  // CloseCircleOutlined,
  DeleteOutlined,
  LinkOutlined,
  LoadingOutlined
} from "@ant-design/icons";
import { DateUtils } from "utils/dateutils";
import DatePickerWithIcon from "components/date-picker-with-icon/date-picker-with-icon";
// import BtnAlternateAssign from "components/widgets/btn-alternate-assign";
import { CloseAndDistributeSubmittal } from "components/close-and-distribute";
import { noAuthClient } from "services/axios";
import { generateS3URL } from "services/file-mgmt";
import { useCIQQuery } from "hooks/ciq-gql-hooks";
import { SUBMITTAL_WORKFLOW_RESPONSES } from "entity-app/graphQL/ciq-feature-queries";
import { FeatureTypes } from "entity-app/constants";
import {
  ALLOWED_FILE_TYPES_STRING,
  validateFileType
} from "utils/file-validation";
import { useSubmmittialDetails } from "../hook/submittal-details-hook";

interface IDocumentObject {
  file: File;
  status: number;
}

type NextAssigneeResult = {
  type?: string;
  foundUser: any;
  userNameStr: string;
};

const EMPTY_NEXT_ASSIGNEE: NextAssigneeResult = {
  foundUser: null,
  userNameStr: ""
};

const formatParticipantUserName = (user: any) =>
  `${user?.first_name ?? ""} ${user?.last_name ?? ""}`;

const buildNextAssigneeFromUser = (user: any): NextAssigneeResult => ({
  type: "actual",
  foundUser: user,
  userNameStr: formatParticipantUserName(user)
});

const isGcReviewOrDistribution = (workflowStatus: any) =>
  workflowStatus === ESubmittalStatus.GC_REVIEW ||
  workflowStatus === ESubmittalStatus.READY_FOR_DISTRIBUTION;

/** Null means "nothing decided at this stage" — the caller keeps looking. */
const getCreateStageAssignee = (
  submittalParticipants: any
): NextAssigneeResult | null => {
  if (!submittalParticipants?.submitter) return EMPTY_NEXT_ASSIGNEE;
  if (submittalParticipants?.submitter?.user) {
    return buildNextAssigneeFromUser(submittalParticipants.submitter.user);
  }
  return null;
};

const getGcReviewStageAssignee = (
  submittalParticipants: any,
  reviewVal: number
): NextAssigneeResult | null => {
  if (reviewVal <= 4) {
    if (submittalParticipants?.drReviewer) {
      return buildNextAssigneeFromUser(submittalParticipants.drReviewer.user);
    }
    return null;
  }

  if (submittalParticipants?.submitter?.user) {
    return buildNextAssigneeFromUser(submittalParticipants.submitter.user);
  }
  return null;
};

const getGcNextAssignee = (
  submittalViewState: any,
  submittal: any,
  submittalParticipants: any,
  reviewVal: number
): NextAssigneeResult | null => {
  if (submittalViewState?.status === ESubmittalStatus.CREATE) {
    const createAssignee = getCreateStageAssignee(submittalParticipants);
    if (createAssignee) return createAssignee;
  }

  if (
    submittalViewState?.status === ESubmittalStatus.SUBMIT &&
    submittalParticipants?.gcReviewer?.user
  ) {
    return buildNextAssigneeFromUser(submittalParticipants.gcReviewer.user);
  }

  if (isGcReviewOrDistribution(submittal?.workflow_status)) {
    const reviewAssignee = getGcReviewStageAssignee(
      submittalParticipants,
      reviewVal
    );
    if (reviewAssignee) return reviewAssignee;
  }

  if (
    submittal?.workflow_status === ESubmittalStatus.EXTERNAL_REVIEW &&
    submittalParticipants?.gcReviewer
  ) {
    return {
      foundUser: submittalParticipants.gcReviewer.user,
      userNameStr: formatParticipantUserName(
        submittalParticipants.gcReviewer.user
      )
    };
  }

  return null;
};

const getGcReviewerNextAssignee = (
  submittalParticipants: any
): NextAssigneeResult | null => {
  if (!submittalParticipants?.gcReviewer) return null;
  return buildNextAssigneeFromUser(submittalParticipants.gcReviewer.user);
};

const getAssigneeActualDateStates = () => {
  return [
    {
      label: "Assigned to Subcontractor on",
      id: 1
    },
    {
      label: "Submitted by Subcontractor on",
      id: 2
    },
    {
      label: "Submitted for Design Review on",
      id: 3
    },
    {
      label: "Design Review Completed on",
      id: 4
    },
    {
      label: "Submittal Distributed on",
      id: 5
    }
  ];
};

function shouldShowWorkflowActions(
  submittalViewState: any,
  submittalParticipants: any
) {
  if (submittalViewState?.status === ESubmittalStatus.DONE) return false;

  // Nothing to act on until a design reviewer exists on a freshly created
  // submittal.
  return !(
    submittalViewState?.status === ESubmittalStatus.CREATE &&
    !submittalParticipants?.drReviewer?.user?.id
  );
}

function isDistributeDisabled(args: {
  isDateblockRecalculating?: boolean;
  isWorkflowInOverrideMode: boolean;
  loggedInUserId: any;
  submittalParticipants: any;
  submittalAssignInProg: boolean;
  isProjectArchived?: boolean;
}) {
  return (
    args.isProjectArchived ||
    args.isDateblockRecalculating ||
    (!args.isWorkflowInOverrideMode &&
      (args.loggedInUserId !==
        args.submittalParticipants?.gcReviewer?.user?.id ||
        args.submittalAssignInProg))
  );
}

function isAssignDisabled(args: {
  isDateblockRecalculating?: boolean;
  isWorkflowInOverrideMode: boolean;
  isRevisionMode: boolean;
  submittalViewState: any;
  isLoggedInUserMainInWorkflow: boolean;
  cannotRespondToWorkflow: boolean;
  editableFields: any[];
  submittalParticipants: any;
  submittalAssignInProg: boolean;
}) {
  return (
    args.isDateblockRecalculating ||
    (!args.isWorkflowInOverrideMode &&
      (args.isRevisionMode ||
        (args.submittalViewState?.status !== ESubmittalStatus.CREATE &&
          !args.isLoggedInUserMainInWorkflow) ||
        args.cannotRespondToWorkflow ||
        !args.editableFields.includes("assign") ||
        !args.submittalParticipants?.gcReviewer?.user?.id ||
        !args.submittalParticipants?.drReviewer?.user?.id ||
        !args.submittalParticipants?.submitter?.user?.id ||
        args.submittalAssignInProg))
  );
}

const recalculatingMsgKey = "recalculatingDateblockKey";

export default function SubmittalWorkflowResponse(
  props: Readonly<{
    actions: TFetureDetailUpdateAction;
    projectId: string;
    gqlClientForProject: any;
    submittal: any;
    editableFields: any[];
    workflowOverride: {
      isWorkflowInOverrideMode: boolean;
      setIsWorkflowInOverrideMode: any;
    };
    isRevisionMode: boolean;
    tokenRetrievalState: any;
    updateFeatureData: {
      localFeatureInstanceData: any;
      setLocalFeatureInstanceData: any;
    };
    submittalViewState: any;
    setIsDateblockRecalculating?: (val: boolean) => void;
    isDateblockRecalculating?: boolean;
    isAttachementFeatureEnabled: boolean;
    submittalHeaderMap: any;
  }>
) {
  const {
    updateFeatureData: { localFeatureInstanceData },
    actions,
    submittal,
    workflowOverride,
    isRevisionMode,
    tokenRetrievalState,
    gqlClientForProject,
    submittalViewState,
    editableFields,
    setIsDateblockRecalculating,
    isDateblockRecalculating,
    isAttachementFeatureEnabled,
    submittalHeaderMap
  } = props;

  const { isWorkflowInOverrideMode, setIsWorkflowInOverrideMode } =
    workflowOverride;
  const { isProjectArchived } = useContext(ProjectContext);
  const [submittalAssignInProg, setSubmittalAssignInProg] = useState(false);
  // const [isAltAssignee, setIsAltAssigne] = useState(false);

  const { Option } = Select;

  const [modal, contextHolder] = Modal.useModal();

  const [currentActualDateState, setCurrentActualDateState] = useState({
    label: "",
    id: -1,
    value: DateUtils.dateTimeObj(),
    previousDate: null as any
  });
  // const [altAssignee, setAltAssignee] = useState({
  //   userName: "",
  //   userId: "",
  //   selected: false
  // });

  const { data: submittalWorkflowResponses } = useCIQQuery(
    SUBMITTAL_WORKFLOW_RESPONSES,
    {
      client: gqlClientForProject,
      skip: !gqlClientForProject
    }
  );

  const {
    showReviewOption,
    contentClassNames,
    assignDisabledTooltipMsg,
    getAttachBtnDisabledMsg,
    cannotRespondToWorkflow,
    isLoggedInUserMainInWorkflow,
    submittalParticipants,
    showOverrideWorkflowBtn,
    loggedInUserId,
    permission: {
      isLoggedInUserGcAdmin,
      isLoggedInUserGC,
      isLoggedInUserSC,
      isCurrentAssigneeGCUser,
      isLoggedInUserWFGCUser,
      isLoggedInUserOwner,
      isLoggedInUserArchitect,
      isLoggedInUserEngineer
    }
  } = useSubmmittialDetails({
    featureInstanceData: localFeatureInstanceData,
    isRevisionMode,
    submittal,
    editableFields,
    submittalViewState,
    isWorkflowInOverrideMode
  });

  const [reviewVal, setReviewVal] = useState(
    submittalWorkflowResponses?.submittal_response?.length
      ? submittalWorkflowResponses?.submittal_response[0]?.id
      : ""
  );
  const [commentVal, setCommentVal] = useState("");
  const [uploadedDocumentList, setUploadedDocumentList] = useState<
    Array<IDocumentObject>
  >([]);

  const [openDistributeModel, setOpenDistributeSubmittalModel] =
    useState(false);

  // const onSelectAltAssign = (assigneeId: string, assigneeUser: string) => {
  //   setAltAssignee({
  //     userName: assigneeUser,
  //     userId: assigneeId,
  //     selected: true
  //   });
  // };
  // const clearAltAssignee = () => {
  //   setAltAssignee({
  //     userName: "",
  //     userId: "",
  //     selected: false
  //   });
  // };

  // const showAdvAssignBtn =
  //   submittalViewState.status !== ESubmittalStatus.DONE &&
  //   loggedInUserId === submittalParticipants?.assignee?.user?.id;

  const updateSubmittalSuccessResp = new Set([
    "Updated Successfully",
    "Ball In Court updated"
  ]);
  const updateSubmittalErrResp = new Set([
    "No Updates Allowed",
    "Update Failed"
  ]);

  const getOnBehalfOfInfoObj = () => {
    let onBehalfOfInfoObj: any = null;
    if (isWorkflowInOverrideMode) {
      onBehalfOfInfoObj = {};
      const assignee = localFeatureInstanceData?.feature_user_paticipants?.find(
        (user: any) =>
          user?.user_participation_type?.id === UserParticipationType.ASSIGNEE
      );
      if (assignee) {
        onBehalfOfInfoObj.on_behalf_of_user_id = assignee?.user?.id;
      }
    }
    return onBehalfOfInfoObj;
  };

  const isCommentDisabled =
    !isWorkflowInOverrideMode &&
    (isRevisionMode ||
      cannotRespondToWorkflow ||
      !editableFields.includes("comment") ||
      !submittalParticipants?.gcReviewer?.user?.id ||
      !submittalParticipants?.drReviewer?.user?.id ||
      !submittalParticipants?.submitter?.user?.id);

  const assigneeActualDateStates = useMemo(() => {
    return getAssigneeActualDateStates();
  }, []);

  useEffect(() => {
    const dateBlocks = localFeatureInstanceData?.workflow_instance_milestones;
    if (dateBlocks?.length > 0) {
      const getIndex = () => {
        if (submittal?.workflow_status === ESubmittalStatus.CREATE) return 0;
        if (submittal?.workflow_status === ESubmittalStatus.SUBMIT) return 1;
        if (submittal?.workflow_status === ESubmittalStatus.GC_REVIEW)
          return reviewVal > 4 ? 0 : 2;
        if (submittal?.workflow_status === ESubmittalStatus.EXTERNAL_REVIEW)
          return 3;
        if (
          submittal?.workflow_status === ESubmittalStatus.READY_FOR_DISTRIBUTION
        )
          return reviewVal > 4 ? 0 : 4;
        return 0;
      };

      const currentStateIndex = getIndex();

      const previousDate =
        currentStateIndex > 0
          ? dateBlocks[currentStateIndex - 1]?.actual_date
          : null;

      setCurrentActualDateState({
        value: previousDate
          ? DateUtils.dateTimeObj(previousDate)
          : DateUtils.dateTimeObj(),
        previousDate,
        ...assigneeActualDateStates[currentStateIndex]
      });
    }
  }, [
    assigneeActualDateStates,
    reviewVal,
    localFeatureInstanceData?.workflow_instance_milestones,
    submittal?.workflow_status
  ]);

  // useEffect(() => {
  //   setIsAltAssigne(false);
  //
  //   if (loggedInUserId === submittalParticipants?.assignee?.user?.id) {
  //     if (
  //       submittal?.workflow_status === ESubmittalStatus.SUBMIT &&
  //       submittalParticipants?.assignee?.user?.id !==
  //         submittalParticipants?.submitter?.user?.id
  //     ) {
  //       setIsAltAssigne(true);
  //     }
  //
  //     if (
  //       submittal?.workflow_status === ESubmittalStatus.GC_REVIEW &&
  //       submittalParticipants?.assignee?.user?.id !==
  //         submittalParticipants?.gcReviewer?.user?.id
  //     ) {
  //       setIsAltAssigne(true);
  //     }
  //
  //     if (
  //       submittal?.workflow_status === ESubmittalStatus.EXTERNAL_REVIEW &&
  //       submittalParticipants?.assignee?.user?.id !==
  //         submittalParticipants?.drReviewer?.user?.id
  //     ) {
  //       setIsAltAssigne(true);
  //     }
  //
  //     if (
  //       submittalViewState?.status ===
  //         ESubmittalStatus.READY_FOR_DISTRIBUTION &&
  //       submittalParticipants?.assignee?.user?.id !==
  //         submittalParticipants?.gcReviewer?.user?.id
  //     ) {
  //       setIsAltAssigne(true);
  //     }
  //   }
  // }, [
  //   loggedInUserId,
  //   submittalViewState?.status,
  //   submittal?.workflow_status,
  //   submittalParticipants?.assignee?.user?.id,
  //   submittalParticipants?.drReviewer?.user?.id,
  //   submittalParticipants?.gcReviewer?.user?.id,
  //   submittalParticipants?.submitter?.user?.id,
  //   submittalParticipants,
  //   submittalViewState
  // ]);

  const markAsDone = useMemo(() => {
    return (
      (isLoggedInUserGC &&
        submittalViewState?.status === ESubmittalStatus.REVIEW &&
        submittal?.submittal_status_history_details?.length &&
        submittal?.submittal_status_history_details[0]?.submittal_response_id &&
        submittal?.submittal_status_history_details[0]?.submittal_response_id <=
          4 &&
        submittal?.submittal_status_history_details[1]?.assignee ===
          submittalParticipants.drReviewer?.user?.id &&
        reviewVal <= 4) ||
      false
    );
  }, [
    isLoggedInUserGC,
    reviewVal,
    submittal?.submittal_status_history_details,
    submittalParticipants.drReviewer?.user?.id,
    submittalViewState?.status
  ]);

  const WFResponses = useMemo(() => {
    if (!submittalWorkflowResponses?.submittal_response) return null;

    const responseState = {
      hasDRRejected: false,
      responses: submittalWorkflowResponses?.submittal_response
    };

    if (submittal?.workflow_status === ESubmittalStatus.GC_REVIEW) {
      const latestSubmittalWFHistoryItem: any =
        submittal?.submittal_status_history_details?.find((history: any) => {
          return history?.status !== null;
        });
      const responseId = latestSubmittalWFHistoryItem?.submittal_response_id;

      if (responseId && responseId > 4) {
        responseState.hasDRRejected = true;
        responseState.responses =
          submittalWorkflowResponses?.submittal_response?.filter(
            (WFResponse: any) => {
              return WFResponse.id > 4;
            }
          );
      }
    }
    return responseState;
  }, [submittalWorkflowResponses?.submittal_response, submittal]);

  const overrideWFConfirmDialog = () => {
    modal.confirm({
      content: "Are you sure you want override the workflow?",
      onOk() {
        setIsWorkflowInOverrideMode(true);
      },
      okText: "Confirm"
    });
  };

  useEffect(() => {
    if (WFResponses) {
      setReviewVal(WFResponses.responses[0]?.id);
    }
  }, [WFResponses]);

  const showDrRejectionMsgToGC =
    (isLoggedInUserGcAdmin &&
      isCurrentAssigneeGCUser &&
      isLoggedInUserWFGCUser &&
      WFResponses?.hasDRRejected) ||
    (isLoggedInUserGcAdmin &&
      isCurrentAssigneeGCUser &&
      !isLoggedInUserWFGCUser &&
      isWorkflowInOverrideMode &&
      WFResponses?.hasDRRejected);

  const getNextAssignee = () => {
    if (isLoggedInUserGC) {
      const gcAssignee = getGcNextAssignee(
        submittalViewState,
        submittal,
        submittalParticipants,
        reviewVal
      );
      if (gcAssignee) return gcAssignee;
    }

    const isNonGcReviewerRole =
      isLoggedInUserSC ||
      isLoggedInUserOwner ||
      isLoggedInUserArchitect ||
      isLoggedInUserEngineer;

    if (isNonGcReviewerRole) {
      const reviewerAssignee = getGcReviewerNextAssignee(submittalParticipants);
      if (reviewerAssignee) return reviewerAssignee;
    }

    return EMPTY_NEXT_ASSIGNEE;
  };

  const actionButtonLabel = () => {
    if (submittal?.workflow_status === ESubmittalStatus.CREATE) {
      return "Assign";
    }
    if (submittal?.workflow_status === ESubmittalStatus.SUBMIT) {
      return reviewVal > 4 ? "Return" : "Submit";
    }
    if (submittal?.workflow_status === ESubmittalStatus.GC_REVIEW) {
      return reviewVal > 4 ? "Return" : "Submit for Review";
    }
    if (submittal?.workflow_status === ESubmittalStatus.EXTERNAL_REVIEW) {
      return reviewVal > 4 ? "Return" : "Submit Review";
    }
    if (
      submittal?.workflow_status === ESubmittalStatus.READY_FOR_DISTRIBUTION
    ) {
      return reviewVal > 4 ? "Return" : "Close and Distribute";
    }
    return "Confirm";
  };

  const getAssigneeRow = () => {
    return (
      <div className="flex items-center justify-between mb-3">
        <div className="review-action">
          {markAsDone ? (
            <b>Close and Distribute</b>
          ) : (
            <span>
              <b>Assign To:</b> {getNextAssignee().userNameStr || "Submitter"}
            </span>
          )}
        </div>
        {showOverrideWorkflowBtn && !isWorkflowInOverrideMode && (
          <div>
            <Button
              onClick={overrideWFConfirmDialog}
              disabled={isRevisionMode || isDateblockRecalculating}
            >
              Override Workflow
            </Button>
          </div>
        )}
      </div>
    );
  };

  const showActionButton = () => {
    return (
      submittal?.workflow_status === ESubmittalStatus.CREATE ||
      isWorkflowInOverrideMode ||
      submittalParticipants?.assignee?.user?.id === loggedInUserId
    );
  };

  const getLatestWFHistoryItem = (historyItems: any) => {
    // unreachable: only caller passes `... || []`, so historyItems is always truthy
    /* v8 ignore next */
    if (!historyItems) return null;
    const historyArr = historyItems.filter(
      (historyItem: any) =>
        !(
          historyItem.action &&
          historyItem.action.toLowerCase().includes("deleted")
        )
    );
    if (historyArr && historyArr?.length > 0) {
      return historyArr[0];
    }
    return null;
  };

  const uploadAttachment = async ({ file, onSuccess }: any) => {
    let requestPayload = {
      feature: "SUBMITTALS",
      feature_id: localFeatureInstanceData?.id,
      file_name: file.name
    };

    const onBehalfOfInfo = getOnBehalfOfInfoObj();
    if (onBehalfOfInfo) {
      requestPayload = { ...requestPayload, ...onBehalfOfInfo };
    }

    const historyItem = getLatestWFHistoryItem(
      submittal?.submittal_status_history_details || []
    );

    const subHistoryObj: any = {};
    if (historyItem) {
      subHistoryObj.submittal_status_history_id = `${historyItem.id}`;
      requestPayload = {
        ...requestPayload,
        ...subHistoryObj
      };
    }
    const signedUrlRespose: any = await generateS3URL(
      requestPayload,
      tokenRetrievalState.token
    );

    if (signedUrlRespose.error) {
      message.error(signedUrlRespose.error.msg);
      return null;
    }

    const fileUploadResponse: any = await noAuthClient.put(
      `${signedUrlRespose.data.success.url}`,
      file,
      {
        onUploadProgress: () => {}
      }
    );
    if (fileUploadResponse.error) {
      message.error(signedUrlRespose.error.msg);
      return null;
      // reject(new Error(fileUploadResponse.error));
    }
    onSuccess("OK");
    return fileUploadResponse;
  };

  const getActingUserContext = () => {
    const assigneeParticipant =
      localFeatureInstanceData?.feature_user_paticipants?.find(
        (user: any) =>
          user?.user_participation_type?.id === UserParticipationType.ASSIGNEE
      );
    const actingUser = submittalParticipants?.assignee?.user;
    const actingUserSourceInfoId =
      assigneeParticipant?.user_source_info_id ??
      assigneeParticipant?.user_source_info?.id ??
      "";
    const actingUsername = actingUser
      ? `${actingUser?.first_name ?? ""} ${actingUser?.last_name ?? ""}`.trim()
      : "";
    const actingUserId = actingUser?.id ?? actingUser?.user_id;
    const selectedResponseObj =
      submittalWorkflowResponses?.submittal_response?.find(
        (r: any) => String(r?.id) === String(reviewVal)
      );
    const responseText = selectedResponseObj?.response ?? "";
    return {
      actingUserSourceInfoId,
      actingUsername,
      responseText,
      actingUserId
    };
  };

  const createResponseMeta = (ctx: {
    actingUserSourceInfoId: string;
    actingUsername: string;
    responseText: string;
    actingUserId: string;
  }) => ({
    user_source_info_id: ctx.actingUserSourceInfoId,
    user_id: ctx.actingUserId,
    username: ctx.actingUsername,
    response: ctx.responseText
  });

  const uploadDocumentsWithStatus = async (
    docs: IDocumentObject[]
  ): Promise<void> => {
    const clone = docs.map((d) => ({ file: d.file, status: d.status }));
    for (const [, doc] of clone.entries()) {
      doc.status = 2;
      setUploadedDocumentList([...clone]);
      try {
        await uploadAttachment({ file: doc.file, onSuccess: () => {} });
        doc.status = 3;
      } catch {
        doc.status = 4;
      }
      setUploadedDocumentList([...clone]);
    }
  };

  const handleUpdateResult = (msg: string) => {
    if (updateSubmittalSuccessResp.has(msg)) {
      message.success({ content: msg, key: recalculatingMsgKey, duration: 3 });
      setIsWorkflowInOverrideMode(false);
      return true;
    }
    if (updateSubmittalErrResp.has(msg)) {
      message.error({ content: msg, key: recalculatingMsgKey, duration: 3 });
      modal.error({ content: msg });
      return false;
    }
    message.destroy(recalculatingMsgKey);
    return true;
  };

  const assignSubmittal = async () => {
    setSubmittalAssignInProg(true);
    setIsDateblockRecalculating?.(true);
    message.loading({
      content: "Recalculating...",
      key: recalculatingMsgKey,
      duration: 60000
    });

    const nextAssigneeUser = getNextAssignee().foundUser;
    const ctx = getActingUserContext();

    const isDistributionFlow =
      markAsDone ||
      (submittalViewState?.status === ESubmittalStatus.READY_FOR_DISTRIBUTION &&
        reviewVal <= 4);

    const updateSubmittalValues: any = {
      feature_instance_id: localFeatureInstanceData?.id,
      submittal_response_id: null,
      on_behalf_of: null,
      comment: commentVal.trim() || "",
      assign_to: nextAssigneeUser?.user_id ?? nextAssigneeUser?.id,
      review_response: null,
      final_response: null
    };

    if (showReviewOption) {
      updateSubmittalValues.submittal_response_id = reviewVal;
      if (ctx.actingUserId) {
        updateSubmittalValues.review_response = createResponseMeta(ctx);
      }
    }
    if (isDistributionFlow && ctx.actingUserId) {
      updateSubmittalValues.final_response = createResponseMeta(ctx);
    }
    if (isWorkflowInOverrideMode && submittalParticipants?.assignee) {
      updateSubmittalValues.on_behalf_of =
        submittalParticipants?.assignee?.user?.id;
    }
    if (isLoggedInUserGcAdmin) {
      updateSubmittalValues.actual_milestone_date =
        DateUtils.formatDateWithLunchTime(currentActualDateState.value);
    }

    try {
      const updateResponse = await actions.updateFeatureInstancesResponse({
        variables: {
          feature_instance_type_id: FeatureTypes.SUBMITTALS,
          feature_instances: [updateSubmittalValues]
        }
      });
      await uploadDocumentsWithStatus(uploadedDocumentList);

      setUploadedDocumentList([]);
      setSubmittalAssignInProg(false);
      const msg = updateResponse?.data?.update_feature_instances?.message;
      const success = handleUpdateResult(msg);
      setIsDateblockRecalculating?.(false);
      if (!success) return;

      setCommentVal("");
    } catch {
      modal.error({ content: "An error occured" });
      message.error({
        content: "An error occured",
        key: recalculatingMsgKey,
        duration: 3
      });
      setSubmittalAssignInProg(false);
      setIsDateblockRecalculating?.(false);
    }
  };

  // const alternateAssignSubmittal = async (assigneeId: string) => {
  //   ... alternate assignee logic commented out ...
  // };

  return (
    <div className="" data-testid="swr-root">
      <Form layout="vertical" data-testid="swr-form">
        <Tooltip
          title={
            isRevisionMode
              ? "Go to the latest revision to respond to the workflow."
              : ""
          }
          arrow={{ pointAtCenter: true }}
        >
          <div
            className={`overflow-auto ${contentClassNames || ""}`}
            data-testid="swr-content"
          >
            <div>
              {showReviewOption && (
                <Tooltip
                  title={assignDisabledTooltipMsg}
                  placement="rightBottom"
                >
                  <Form.Item
                    label={
                      actionButtonLabel() === "Close and Distribute"
                        ? submittalHeaderMap?.final_response
                        : submittalHeaderMap?.review_response
                    }
                    className="!leading-none"
                  >
                    <Select
                      value={reviewVal}
                      onChange={(val) => {
                        setReviewVal(val);
                      }}
                      disabled={
                        isDateblockRecalculating ||
                        (!isWorkflowInOverrideMode &&
                          (isRevisionMode ||
                            cannotRespondToWorkflow ||
                            !editableFields.includes("review_status")))
                      }
                      data-testid="swr-select-response"
                    >
                      {WFResponses?.responses?.map((response: any) => {
                        return (
                          <Option key={response.id} value={response.id}>
                            {response.response}
                          </Option>
                        );
                      })}
                    </Select>
                    {showDrRejectionMsgToGC && (
                      <span className="text-xs text-amber-600">
                        The submittal has been rejected by the Design Reviewer.
                        You have to reject and reassign the submittal to the
                        subcontractor.
                      </span>
                    )}
                  </Form.Item>
                </Tooltip>
              )}

              <div>
                <Tooltip
                  title={assignDisabledTooltipMsg}
                  placement="rightBottom"
                >
                  <Form.Item label="Comment">
                    <TextArea
                      rows={2}
                      value={commentVal}
                      onChange={(event) => {
                        setCommentVal(event.target.value);
                      }}
                      disabled={isCommentDisabled || isDateblockRecalculating}
                      data-testid="swr-textarea-comment"
                    />
                  </Form.Item>
                </Tooltip>
              </div>
              <div>
                {uploadedDocumentList.map(
                  (docs: IDocumentObject, index: number) => {
                    return (
                      <div
                        key={`${docs.file.name}-${index}`}
                        className="border-solid border border-neutral-200 workflow-document-prereview"
                        data-testid="swr-uploaded-item"
                      >
                        {docs.status === 2 ? (
                          <LoadingOutlined className="mr-2" />
                        ) : docs.status === 3 ? (
                          <CheckCircleFilled className="mr-2" />
                        ) : (
                          <LinkOutlined className="mr-2" />
                        )}
                        <div className="workflow-document-prereview--title truncate">
                          {docs.file.name}
                        </div>
                        <DeleteOutlined
                          onClick={() => {
                            setUploadedDocumentList(
                              uploadedDocumentList.filter(
                                (_: IDocumentObject, itemIndex: number) =>
                                  itemIndex !== index
                              )
                            );
                          }}
                          data-testid="swr-button-remove-uploaded"
                        />
                      </div>
                    );
                  }
                )}
              </div>
              {isLoggedInUserGcAdmin && !isCommentDisabled && (
                <div className="mb-1 flex py-1 space-x-1">
                  <div>{currentActualDateState?.label}</div>
                  <div className="font-semibold">
                    {DateUtils.format(currentActualDateState?.value)}
                  </div>
                  <DatePickerWithIcon
                    value={currentActualDateState?.value}
                    disabledDate={(current) => {
                      if (currentActualDateState?.previousDate) {
                        const previousDate = DateUtils.dateTimeObj(
                          currentActualDateState?.previousDate
                        );
                        return previousDate.isAfter(current);
                      }
                      return false;
                    }}
                    onChange={(val) => {
                      if (val)
                        setCurrentActualDateState((prev) => ({
                          ...prev,
                          value: val
                        }));
                    }}
                    data-testid="swr-datepicker-actual"
                  />
                </div>
              )}
              {isAttachementFeatureEnabled && (
                <div className="mb-2 flex justify-end">
                  <Upload
                    showUploadList={false}
                    multiple
                    beforeUpload={validateFileType}
                    accept={ALLOWED_FILE_TYPES_STRING}
                    customRequest={({ file }: any) => {
                      setUploadedDocumentList([
                        ...uploadedDocumentList,
                        {
                          file,
                          status: 1
                        }
                      ]);
                    }}
                    data-testid="swr-upload"
                  >
                    <Button
                      size="small"
                      type="text"
                      className="workflow-document-upload"
                      title={getAttachBtnDisabledMsg()}
                      disabled={
                        isDateblockRecalculating ||
                        (!isWorkflowInOverrideMode &&
                          (submittalAssignInProg ||
                            isRevisionMode ||
                            (submittalViewState?.status !==
                              ESubmittalStatus.CREATE &&
                              !isLoggedInUserMainInWorkflow) ||
                            cannotRespondToWorkflow ||
                            !editableFields.includes("assign") ||
                            !submittalParticipants?.gcReviewer?.user?.id ||
                            !submittalParticipants?.drReviewer?.user?.id ||
                            !submittalParticipants?.submitter?.user?.id))
                      }
                      data-testid="swr-button-add-attachment"
                    >
                      Add Attachment
                    </Button>
                  </Upload>
                </div>
              )}
              {shouldShowWorkflowActions(
                submittalViewState,
                submittalParticipants
              ) ? (
                <>
                  {getAssigneeRow()}
                  {showActionButton() ? (
                    <div className="flex justify-end">
                      {markAsDone ? (
                        <>
                          <Button
                            type="primary"
                            onClick={() => {
                              setOpenDistributeSubmittalModel(true);
                            }}
                            disabled={isDistributeDisabled({
                              isDateblockRecalculating,
                              isWorkflowInOverrideMode,
                              loggedInUserId,
                              submittalParticipants,
                              submittalAssignInProg,
                              isProjectArchived
                            })}
                          >
                            {actionButtonLabel()}
                          </Button>
                          <Modal
                            title="Submittal Summary"
                            width={500}
                            open={openDistributeModel}
                            onOk={() => {
                              setOpenDistributeSubmittalModel(false);
                              assignSubmittal();
                            }}
                            onCancel={() => {
                              setOpenDistributeSubmittalModel(false);
                            }}
                            okText="Close and Distribute"
                            destroyOnHidden
                            data-testid="swr-modal-distribute"
                          >
                            <CloseAndDistributeSubmittal
                              submittalData={localFeatureInstanceData}
                              submittalWorkflowResponses={
                                submittalWorkflowResponses?.submittal_response
                              }
                              reviewVal={reviewVal}
                              submittalViewState={{
                                status: submittalViewState?.status,
                                userType: ""
                              }}
                              commentVal={commentVal}
                              data-testid="swr-distribute-component"
                            />
                          </Modal>
                        </>
                      ) : (
                        <Button
                          type="primary"
                          onClick={() => {
                            assignSubmittal();
                          }}
                          disabled={isAssignDisabled({
                            isDateblockRecalculating,
                            isWorkflowInOverrideMode,
                            isRevisionMode,
                            submittalViewState,
                            isLoggedInUserMainInWorkflow,
                            cannotRespondToWorkflow,
                            editableFields,
                            submittalParticipants,
                            submittalAssignInProg
                          })}
                          loading={submittalAssignInProg}
                          title={assignDisabledTooltipMsg}
                          data-testid="swr-button-action"
                        >
                          {actionButtonLabel()}
                        </Button>
                      )}
                    </div>
                  ) : (
                    ""
                  )}
                </>
              ) : (
                ""
              )}
            </div>
          </div>
          {contextHolder}
        </Tooltip>
      </Form>
    </div>
  );
}
